import { FileExtension } from "@inrev/common";
import { useState } from "react";
import { type AttachmentContext, trackAttachmentAction } from "../../../analytics";
import { ConfirmationModalClickWrapper } from "../../layout/ConfirmationModalClickWrapper";
import { type PDFViewerControl } from "../../patterns/pdf-viewer";
import { ClickableIcon } from "../../primitives";
import { PDFViewerModal } from "../PDFViewerModal";
import { Spinner } from "../Spinner";

export type AttachmentActionsProps<
	TDelete extends boolean,
	TDownload extends boolean,
	TView extends boolean,
> = {
	fileName: string;
	fileExtension: FileExtension;
	enableDelete: TDelete;
	enableDownload: TDownload;
	enableView: TView;
	analyticsContext: AttachmentContext;
} & (TDelete extends true ? { onConfirmDelete: () => void } : { onConfirmDelete?: never }) &
	(TDownload extends true
		? { onConfirmDownload: () => void; isLoading: boolean }
		: { onConfirmDownload?: never; isLoading?: never }) &
	(TView extends true ? { viewControl: PDFViewerControl } : { viewControl?: never });

export const AttachmentActions = <
	TDelete extends boolean,
	TDownload extends boolean,
	TView extends boolean,
>({
	fileName,
	fileExtension,
	enableDelete,
	onConfirmDelete,
	enableDownload,
	isLoading,
	onConfirmDownload,
	enableView,
	viewControl,
	analyticsContext,
}: AttachmentActionsProps<TDelete, TDownload, TView>) => {
	const [openPDFViewer, setOpenPDFViewer] = useState(false);

	return (
		<div className="flex items-start justify-center flex-row gap-x-[10px] flex-shrink-0">
			{enableDelete && (
				<ConfirmationModalClickWrapper
					message={
						<div className="w-[375px] text-center break-words">
							Are you sure you want to delete
							<br />
							{fileName}.{fileExtension}?
						</div>
					}
					className="items-center"
					confirmButtonText="Delete"
					confirmationButtonColor="danger"
					confirmTrackingId={`${analyticsContext}-attachment-delete-confirm`}
					cancelTrackingId={`${analyticsContext}-attachment-delete-cancel`}
					onConfirm={() => {
						trackAttachmentAction("delete", analyticsContext);
						onConfirmDelete?.();
					}}
				>
					<ClickableIcon
						type="trash"
						color="danger"
						size="small"
						trackingId={`${analyticsContext}-attachment-delete`}
					/>
				</ConfirmationModalClickWrapper>
			)}
			{enableView && (
				<ClickableIcon
					onClick={() => {
						trackAttachmentAction("preview", analyticsContext);
						setOpenPDFViewer(true);
					}}
					type="eye"
					color="secondary"
					size="small"
					trackingId={`${analyticsContext}-attachment-preview`}
				/>
			)}
			{enableDownload && (
				<>
					{isLoading ? (
						<Spinner
							spinnerClassName="w-[19px] h-[19px] border-gray-500 border-t-transparent"
							containerClassName="w-[19px] h-[19px]"
						/>
					) : (
						<ClickableIcon
							type="arrow-down-tray"
							color="secondary"
							size="small"
							onClick={() => {
								trackAttachmentAction("download", analyticsContext);
								onConfirmDownload?.();
							}}
							trackingId={`${analyticsContext}-attachment-download`}
						/>
					)}
				</>
			)}
			{openPDFViewer && enableView && (
				<PDFViewerModal
					onClose={() => setOpenPDFViewer(false)}
					control={viewControl}
					header={fileName}
					trackingId={`${analyticsContext}-attachment-viewer`}
				/>
			)}
		</div>
	);
};
